import { useEffect, useRef, useState, useCallback } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@chakra-ui/react';
import { Plus, TrashSimple, MagnifyingGlass, DotsSixVertical, Barbell } from '@phosphor-icons/react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import { applyServerErrors } from '../../../shared/CustomFormV3/serverErrors';
import toast from 'react-hot-toast';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IBlock, IExerciseInBlock, IProgrammingExercise } from '../../../models/programming';
import {
  BLOCK_TYPE,
  BLOCK_TYPE_COLORS,
  BLOCK_TYPE_LABELS,
  LOAD_UNIT,
  LOAD_UNIT_LABELS,
  EXERCISE_METHOD,
  EXERCISE_METHOD_LABELS,
  getLoadUnitsForInputMode,
  getDefaultLoadUnit,
} from '../../../enums/programming';
import styles from './BlockBuilderModal.module.scss';

interface BlockBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  block: IBlock | null;
}

interface ExerciseRowForm {
  exerciseId: string;
  exerciseName: string;
  defaultInputMode?: string;
  sets: number | string;
  reps: string;
  loadValue: number | string;
  loadUnit: LOAD_UNIT;
  rest: string;
  method: EXERCISE_METHOD | '';
}

interface BlockFormValues {
  title: string;
  type: BLOCK_TYPE;
  tags: string[];
  exercises: ExerciseRowForm[];
  saveAsTemplate: boolean;
}

const DEFAULT_EXERCISE_ROW: ExerciseRowForm = {
  exerciseId: '',
  exerciseName: '',
  sets: 3,
  reps: '',
  loadValue: '',
  loadUnit: LOAD_UNIT.KG,
  rest: '',
  method: '',
};

const BlockBuilderModal = ({ isOpen, onClose, block }: BlockBuilderModalProps) => {
  const queryClient = useQueryClient();
  const { getProgrammingExercises, postProgrammingBlock, patchProgrammingBlock } = useEndpoints();

  const isEdit = block !== null;

  const { control, register, handleSubmit, reset, setValue, setError, watch, formState: { errors } } = useForm<BlockFormValues>({
    defaultValues: {
      title: '',
      type: BLOCK_TYPE.WARMUP,
      tags: [],
      exercises: [{ ...DEFAULT_EXERCISE_ROW }],
      saveAsTemplate: true,
    },
  });

  const [tagInput, setTagInput] = useState('');
  const watchedTags = watch('tags');

  const { fields, append, remove, move } = useFieldArray({ control, name: 'exercises' });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = fields.findIndex(f => f.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        move(oldIndex, newIndex);
      }
    }
  };

  const watchedType = useWatch({ control, name: 'type' });
  const blockColor = BLOCK_TYPE_COLORS[watchedType] || '#757575';

  useEffect(() => {
    if (!isOpen) return;

    setTagInput('');
    if (block) {
      reset({
        title: block.title,
        type: block.type,
        tags: block.tags ?? [],
        saveAsTemplate: block.isTemplate,
        exercises: block.exercises.length > 0
          ? block.exercises.map((ex) => {
              // exerciseId può essere populated (oggetto) o stringa
              const exIdRaw = ex.exerciseId as any;
              const isPopulated = typeof exIdRaw === 'object' && exIdRaw !== null;
              const exerciseObj = isPopulated ? exIdRaw : ex.exercise;
              const id = isPopulated ? exIdRaw._id : ex.exerciseId;
              const name = exerciseObj?.name?.it || exerciseObj?.name?.en || id;

              return {
                exerciseId: id,
                exerciseName: name,
                defaultInputMode: exerciseObj?.defaultInputMode,
                sets: ex.sets,
                reps: ex.reps,
                loadValue: ex.loads?.[0]?.value ?? '',
                loadUnit: ex.loads?.[0]?.unit ?? LOAD_UNIT.KG,
                rest: ex.rest ?? '',
                method: ex.method ?? '',
              };
            })
          : [{ ...DEFAULT_EXERCISE_ROW }],
      });
    } else {
      reset({
        title: '',
        type: BLOCK_TYPE.WARMUP,
        tags: [],
        exercises: [{ ...DEFAULT_EXERCISE_ROW }],
        saveAsTemplate: true,
      });
    }
  }, [isOpen, block, reset]);

  const saveMutation = useMutation({
    mutationFn: async (values: BlockFormValues) => {
      const tags = values.tags.filter(Boolean);

      const exercises: Partial<IExerciseInBlock>[] = values.exercises
        .filter((ex) => ex.exerciseId)
        .map((ex, i) => ({
          exerciseId: ex.exerciseId,
          order: i,
          sets: Number(ex.sets) || 0,
          reps: ex.reps,
          loads: ex.loadValue !== '' && ex.loadValue !== undefined
            ? [{ value: Number(ex.loadValue), unit: ex.loadUnit }]
            : [],
          rest: ex.rest || undefined,
          method: ex.method || undefined,
        }));

      const body: Partial<IBlock> = {
        title: values.title,
        type: values.type,
        color: BLOCK_TYPE_COLORS[values.type],
        tags,
        exercises: exercises as IExerciseInBlock[],
        isTemplate: values.saveAsTemplate,
      };

      if (isEdit && block) {
        return patchProgrammingBlock(block._id, body);
      }
      return postProgrammingBlock(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programming-blocks'] });
      onClose();
    },
    onError: (error: any) => {
      // 400 di validazione: solo `title` renderizza l'errore inline, il resto va a toast
      if (applyServerErrors(error, setError, { knownFields: ['title'] })) {
        toast.error('Controlla i campi evidenziati');
        return;
      }
      toast.error(
        typeof error?.response?.data?.message === 'string'
          ? error.response.data.message
          : 'Errore salvataggio blocco',
      );
    },
  });

  const onSubmit = (values: BlockFormValues) => {
    saveMutation.mutate(values);
  };

  const Header = (
    <div className={styles.header}>
      <div className={styles.headerColorBar} style={{ background: blockColor }} />
      <div className={styles.headerContent}>
        <div className={styles.headerTitleRow}>
          <div className={styles.headerIcon} style={{ background: blockColor + '1A' }}>
            <Barbell size={20} weight="bold" style={{ color: blockColor }} />
          </div>
          <div>
            <p className={styles.headerTitle}>{isEdit ? 'Modifica blocco' : 'Nuovo blocco'}</p>
            <p className={styles.headerSubtitle}>
              {isEdit ? 'Modifica gli esercizi e i parametri del blocco' : 'Configura tipo, esercizi e parametri'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div className={styles.footer}>
      <Controller
        control={control}
        name="saveAsTemplate"
        render={({ field }) => (
          <label className={styles.templateCheckbox}>
            <input
              type="checkbox"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
            <span>Salva come template</span>
          </label>
        )}
      />
      <div className={styles.footerActions}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onClose}
          disabled={saveMutation.isPending}
        >
          Annulla
        </button>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={handleSubmit(onSubmit)}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? <Spinner size="xs" /> : null}
          {isEdit ? 'Aggiorna blocco' : 'Crea blocco'}
        </button>
      </div>
    </div>
  );

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      Header={Header}
      Footer={Footer}
      isLoading={saveMutation.isPending}
    >
      <div className={styles.body}>
        {/* --- Info generali --- */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Informazioni generali</p>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.label}>Titolo *</label>
              <input
                {...register('title', { required: true })}
                className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
                placeholder="Es. Forza massima — Lower body"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Tipo blocco</label>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <div className={styles.typeSelectWrapper}>
                    <span
                      className={styles.typeColorDot}
                      style={{ background: BLOCK_TYPE_COLORS[field.value] }}
                    />
                    <select {...field} className={styles.select}>
                      {Object.values(BLOCK_TYPE).map((t) => (
                        <option key={t} value={t}>
                          {BLOCK_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Tag</label>
              <div className={styles.tagsContainer}>
                {watchedTags.map((tag, i) => (
                  <span key={i} className={styles.tagChip}>
                    {tag}
                    <button
                      type="button"
                      className={styles.tagChipRemove}
                      onClick={() => setValue('tags', watchedTags.filter((_, idx) => idx !== i))}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  className={styles.tagInput}
                  placeholder={watchedTags.length === 0 ? 'Aggiungi tag...' : ''}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                      e.preventDefault();
                      const newTag = tagInput.trim().replace(/,$/,'');
                      if (newTag && !watchedTags.includes(newTag)) {
                        setValue('tags', [...watchedTags, newTag]);
                      }
                      setTagInput('');
                    }
                    if (e.key === 'Backspace' && !tagInput && watchedTags.length > 0) {
                      setValue('tags', watchedTags.slice(0, -1));
                    }
                  }}
                  onBlur={() => {
                    if (tagInput.trim()) {
                      const newTag = tagInput.trim();
                      if (!watchedTags.includes(newTag)) {
                        setValue('tags', [...watchedTags, newTag]);
                      }
                      setTagInput('');
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        {/* --- Esercizi --- */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <p className={styles.sectionLabel}>Esercizi</p>
            <span className={styles.sectionCount}>{fields.length}</span>
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
              <div className={styles.exercisesList}>
                {fields.map((field, index) => (
                  <SortableExerciseRow
                    key={field.id}
                    id={field.id}
                    index={index}
                    control={control}
                    register={register}
                    setValue={setValue}
                    onRemove={() => remove(index)}
                    canRemove={fields.length > 1}
                    getProgrammingExercises={getProgrammingExercises}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <button
            type="button"
            className={styles.addExerciseBtn}
            onClick={() => append({ ...DEFAULT_EXERCISE_ROW })}
          >
            <Plus size={14} weight="bold" />
            Aggiungi esercizio
          </button>
        </div>
      </div>
    </GenericModalV2>
  );
};

// --- LoadUnitSelect: dropdown unità filtrate per input mode esercizio ---

const LoadUnitSelect = ({ index, control }: { index: number; control: any }) => {
  const inputMode = useWatch({
    control,
    name: `exercises.${index}.defaultInputMode`,
  });
  return (
    <Controller
      control={control}
      name={`exercises.${index}.loadUnit`}
      render={({ field }) => (
        <select {...field} className={styles.paramSelect}>
          {getLoadUnitsForInputMode(inputMode).map((u) => (
            <option key={u} value={u}>
              {LOAD_UNIT_LABELS[u]}
            </option>
          ))}
        </select>
      )}
    />
  );
};

// --- ExerciseRow sub-component ---

interface ExerciseRowProps {
  id: string;
  index: number;
  control: any;
  register: any;
  setValue: any;
  onRemove: () => void;
  canRemove: boolean;
  getProgrammingExercises: (params?: { search?: string; page?: number; limit?: number }) => Promise<any>;
}

const SortableExerciseRow = (props: ExerciseRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ExerciseRow {...props} dragListeners={listeners} dragAttributes={attributes} />
    </div>
  );
};

interface InternalExerciseRowProps extends ExerciseRowProps {
  dragListeners?: any;
  dragAttributes?: any;
}

const ExerciseRow = ({ index, control, register, setValue, onRemove, canRemove, getProgrammingExercises, dragListeners, dragAttributes }: InternalExerciseRowProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<IProgrammingExercise[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleSearchChange = useCallback((value: string, onChange: (val: string) => void) => {
    setSearchQuery(value);
    onChange(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { data } = await getProgrammingExercises({ search: value, limit: 10 });
        setResults(data.docs ?? []);
        setDropdownOpen(true);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [getProgrammingExercises]);

  const handleSelect = useCallback(
    (
      exercise: IProgrammingExercise,
      onExerciseIdChange: (val: string) => void,
      onNameChange: (val: string) => void,
      rowIndex?: number,
    ) => {
      const name = exercise.name.it || exercise.name.en;
      onExerciseIdChange(exercise._id);
      onNameChange(name);
      if (typeof rowIndex === 'number') {
        const inputMode = exercise.defaultInputMode;
        setValue(`exercises.${rowIndex}.defaultInputMode`, inputMode);
        // Reset unità di carico al default coerente con input mode esercizio
        setValue(`exercises.${rowIndex}.loadUnit`, getDefaultLoadUnit(inputMode));
      }
      setSearchQuery(name);
      setDropdownOpen(false);
      setResults([]);
    },
    [setValue],
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.exerciseCard}>
      {/* Top row: order + name + delete */}
      <div className={styles.exerciseTopRow}>
        <div className={styles.exerciseLeftHandle} {...dragListeners} {...dragAttributes}>
          <DotsSixVertical size={16} weight="bold" className={styles.dragIcon} />
          <span className={styles.exerciseOrder}>{index + 1}</span>
        </div>

        <Controller
          control={control}
          name={`exercises.${index}.exerciseId`}
          render={({ field: exerciseIdField }) => (
            <Controller
              control={control}
              name={`exercises.${index}.exerciseName`}
              render={({ field: nameField }) => (
                <div className={styles.exerciseSearchWrapper} ref={wrapperRef}>
                  <MagnifyingGlass size={14} className={styles.exerciseSearchIcon} weight="bold" />
                  <input
                    className={styles.exerciseSearchInput}
                    placeholder="Cerca esercizio..."
                    value={searchQuery || nameField.value}
                    onChange={(e) => handleSearchChange(e.target.value, nameField.onChange)}
                    onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
                    autoComplete="off"
                  />
                  {isSearching && (
                    <div className={styles.searchSpinner}>
                      <Spinner size="xs" />
                    </div>
                  )}
                  {dropdownOpen && results.length > 0 && (
                    <div className={styles.dropdown}>
                      {results.map((ex) => (
                        <button
                          key={ex._id}
                          type="button"
                          className={styles.dropdownItem}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelect(ex, exerciseIdField.onChange, nameField.onChange, index);
                          }}
                        >
                          <span className={styles.dropdownItemName}>{ex.name.it || ex.name.en}</span>
                          {ex.groups?.[0] && (
                            <span className={styles.dropdownItemGroup}>
                              {ex.groups[0].name.it || ex.groups[0].name.en}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            />
          )}
        />

        <button
          type="button"
          className={styles.deleteExerciseBtn}
          onClick={onRemove}
          disabled={!canRemove}
          title="Rimuovi esercizio"
        >
          <TrashSimple size={15} />
        </button>
      </div>

      {/* Bottom row: params grid */}
      <div className={styles.exerciseParamsRow}>
        <div className={styles.paramField}>
          <label className={styles.paramLabel}>Serie</label>
          <input
            {...register(`exercises.${index}.sets`)}
            type="number"
            min={1}
            className={styles.paramInput}
            placeholder="3"
          />
        </div>

        <div className={styles.paramField}>
          <label className={styles.paramLabel}>Reps</label>
          <input
            {...register(`exercises.${index}.reps`)}
            className={styles.paramInput}
            placeholder="8-12"
          />
        </div>

        <div className={styles.paramField} style={{ minWidth: 150 }}>
          <label className={styles.paramLabel}>Carico</label>
          <div className={styles.loadRow}>
            <input
              {...register(`exercises.${index}.loadValue`)}
              type="number"
              min={0}
              step="any"
              className={styles.paramInput}
              placeholder="—"
            />
            <LoadUnitSelect index={index} control={control} />
            <input
              type="hidden"
              {...register(`exercises.${index}.defaultInputMode` as const)}
            />
          </div>
        </div>

        <div className={styles.paramField}>
          <label className={styles.paramLabel}>Recupero</label>
          <input
            {...register(`exercises.${index}.rest`)}
            className={styles.paramInput}
            placeholder="90s"
          />
        </div>

        <div className={styles.paramField}>
          <label className={styles.paramLabel}>Metodo</label>
          <Controller
            control={control}
            name={`exercises.${index}.method`}
            render={({ field }) => (
              <select {...field} className={styles.paramSelect}>
                <option value="">Standard</option>
                {Object.values(EXERCISE_METHOD).filter(m => m !== 'standard').map((m) => (
                  <option key={m} value={m}>{EXERCISE_METHOD_LABELS[m]}</option>
                ))}
              </select>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default BlockBuilderModal;
